import React from 'react';
import { useTranslation } from "react-i18next";
import { Grid } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import DyanamicDropdown from 'components/Elements/DynamicDropdown';
import { useFilters } from '../FilterContext';
import { useSites } from './hooks/useSites';
import { useFilterInitialization } from './hooks/useFilterInitialization';
import { SHOW_ALL_OPTION } from '../constants';
import { getLocalStorageData } from 'helpers/utils';

export const SiteFilter = () => {
	const { t } = useTranslation();
	const { filterValues, updateFilter, isShowAll, config, initialValues } = useFilters();

	const clientUserId = getLocalStorageData('clientUserId');
	const customCaptions = getLocalStorageData('me')?.customCaptions;

	const multiSelect = config.filters?.site?.multiSelect || false;
	const filterKey = 'site';
	const sites = useSites(clientUserId, isShowAll(filterKey));

	useFilterInitialization(filterKey, sites, {
		waitForData: true,
		persistAll: true,
		multiSelect,
		getDefaultValue: () => {
			// Check if 'all' is stored in initialValues for multi-select
			const storedValue = initialValues?.site;

			if (multiSelect) {
				// If 'all' is stored, use the show all option
				if (storedValue === 'all') {
					return [SHOW_ALL_OPTION];
				}
				// Default to show all for multi-select
				return [SHOW_ALL_OPTION];
			} else {
				// For single select, handle 'all' the same way
				if (storedValue === 'all') {
					return SHOW_ALL_OPTION;
				}
				return SHOW_ALL_OPTION;
			}
		},
		getParams: (value) => {
			if (multiSelect) {
				if (!value || value.length === 0) return {};
				// Check if Show All is selected (empty id)
				const hasShowAll = value.some(site => site.id === '' || site.id === 'all');
				if (hasShowAll) {
					// If Show All is selected, pass empty params for API call
					return {};
				}
				const siteIds = value.map(site => site.id).filter(id => id !== '' && id != null);
				return siteIds.length === 0 ? {} : { siteIds: siteIds };
			}
			return value?.id && value.id !== '' ? { siteId: value.id } : {};
		}
	});

	const handleChange = (selection) => {
		const params = multiSelect
			? (() => {
					// Check if Show All is selected (empty id)
					const hasShowAll = selection.some(site => site.id === '' || site.id === 'all');
					if (hasShowAll) {
						// If Show All is selected, pass empty params for API call
						return {};
					}
					const siteIds = selection.map(site => site.id).filter(id => id !== '' && id != null);
					return siteIds.length === 0 ? {} : { siteIds: siteIds };
				})()
			: (selection?.id && selection.id !== '' ? { siteId: selection.id } : {});

		updateFilter(filterKey, selection, params, true);
	};

	const getPlaceholder = () => {
		if (!multiSelect) return t("placeholder.selectEntity", { entity: customCaptions?.site || 'Site' });
		const selected = filterValues[filterKey]?.value || [];
		return selected.length === 0
			? t("label.showAll")
			: t("placeholder.entitiesSelected", { count: selected.length, entity: customCaptions?.site || 'Site' });
	};

	return (
		<Grid item lg={3} md={6} xs={12}>
			<DyanamicDropdown
				dataSource={sites}
				columns={[{ name: 'name', id: 1 }]}
				showHeader={false}
				width="100%"
				placeholder={getPlaceholder()}
				onChange={handleChange}
				selectdValueToshow="name"
				selectedValue={multiSelect ? undefined : filterValues[filterKey]?.value}
				selectedValues={multiSelect ? filterValues[filterKey]?.value : undefined}
				label={t("label.filterByEntity", { entity: customCaptions?.site || 'Site' })}
				icon={<FilterListIcon style={{ color: 'rgb(48,122,215)' }} />}
				required={false}
				showBorderColor
				multiSelect={multiSelect}
				maxDisplayItems={2}
				showSearchBox={true}
			/>
		</Grid>
	);
};
